import { useState } from "react";
import {
  DEFAULT_KONASEEMA_GUIDE,
  buildKonaseemaGuide,
  primeGuideContent,
  type KonaseemaGuide,
} from "@/lib/publicGuide";
import { saveSiteSetting } from "@/lib/adminSettings";
import { btnPrimary } from "@/pages/admin/components/AdminUI";
import { useAdminToast } from "@/hooks/useAdminToast";
import {
  AddRowButton,
  LabeledInput,
  LabeledSelect,
  LabeledTextarea,
  RowCard,
  SectionCard,
} from "./EditorKit";

type Fact = KonaseemaGuide["facts"][number];
type Season = KonaseemaGuide["seasons"][number];
type Activity = KonaseemaGuide["activities"][number];
type Reach = KonaseemaGuide["reach"][number];
type GuideImage = KonaseemaGuide["gallery"][number];

export default function KonaseemaEditor({ initial }: { initial: unknown }) {
  const [guide, setGuide] = useState<KonaseemaGuide>(() => buildKonaseemaGuide(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { notify, node } = useAdminToast();

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await saveSiteSetting("konaseema", guide);
      primeGuideContent({ konaseema: guide });
      notify("Konaseema guide updated.");
    } catch {
      setError("We couldn't save the guide. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const update = <K extends keyof KonaseemaGuide>(key: K, value: KonaseemaGuide[K]) =>
    setGuide((state) => ({ ...state, [key]: value }));

  const resetSection = <K extends keyof KonaseemaGuide>(key: K) =>
    update(key, DEFAULT_KONASEEMA_GUIDE[key]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
        <p className="text-sm text-foreground-600">
          <i className="ri-information-line"></i> The travel guide, its photos, seasons and activities every visitor reads.
        </p>
        <button type="button" onClick={save} disabled={saving} className={btnPrimary}>
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          Save guide
        </button>
      </div>

      {error ? (
        <p className="flex items-center gap-2 text-sm text-accent-900 bg-accent-100 border border-accent-300 rounded-lg px-4 py-3">
          <i className="ri-error-warning-line"></i> {error}
        </p>
      ) : null}

      {/* Intro */}
      <SectionCard icon="ri-file-text-line" title="Introduction" subtitle="The opening paragraphs of the guide.">
        <div className="space-y-3">
          {guide.intro.map((paragraph, index) => (
            <RowCard key={index} onRemove={() => update("intro", guide.intro.filter((_, i) => i !== index))}>
              <LabeledTextarea
                label={`Paragraph ${index + 1}`}
                value={paragraph}
                onChange={(value) => update("intro", guide.intro.map((p, i) => (i === index ? value : p)))}
                rows={3}
              />
            </RowCard>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <AddRowButton onClick={() => update("intro", [...guide.intro, ""])} label="Add paragraph" />
          <button
            type="button"
            onClick={() => resetSection("intro")}
            className="mt-3 text-xs font-semibold text-foreground-500 underline cursor-pointer"
          >
            Reset to default
          </button>
        </div>
      </SectionCard>

      {/* Facts */}
      <SectionCard icon="ri-list-check-2" title="Quick Facts" subtitle="The fact strip shown near the top of the guide.">
        <div className="space-y-3">
          {guide.facts.map((fact, index) => (
            <RowCard key={index} onRemove={() => update("facts", guide.facts.filter((_, i) => i !== index))}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <LabeledInput
                  label="Icon"
                  value={fact.icon}
                  onChange={(value) => update("facts", guide.facts.map((f, i) => (i === index ? { ...f, icon: value } : f)))}
                  placeholder="ri-map-2-line"
                />
                <LabeledInput
                  label="Label"
                  value={fact.label}
                  onChange={(value) => update("facts", guide.facts.map((f, i) => (i === index ? { ...f, label: value } : f)))}
                />
                <LabeledInput
                  label="Value"
                  value={fact.value}
                  onChange={(value) => update("facts", guide.facts.map((f, i) => (i === index ? { ...f, value } : f)))}
                />
              </div>
            </RowCard>
          ))}
        </div>
        <AddRowButton
          onClick={() => update("facts", [...guide.facts, { icon: "ri-map-2-line", label: "", value: "" } as Fact])}
          label="Add fact"
        />
      </SectionCard>

      {/* Seasons */}
      <SectionCard icon="ri-sun-line" title="Seasons" subtitle="The interactive best-time-to-visit switcher.">
        <div className="space-y-3">
          {guide.seasons.map((season, index) => (
            <RowCard key={index} onRemove={() => update("seasons", guide.seasons.filter((_, i) => i !== index))}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <LabeledInput
                  label="Name"
                  value={season.name}
                  onChange={(value) => update("seasons", guide.seasons.map((s, i) => (i === index ? { ...s, name: value } : s)))}
                />
                <LabeledInput
                  label="Months"
                  value={season.months}
                  onChange={(value) => update("seasons", guide.seasons.map((s, i) => (i === index ? { ...s, months: value } : s)))}
                />
                <LabeledInput
                  label="Icon"
                  value={season.icon}
                  onChange={(value) => update("seasons", guide.seasons.map((s, i) => (i === index ? { ...s, icon: value } : s)))}
                  placeholder="ri-sun-line"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <LabeledInput
                  label="Weather"
                  value={season.weather}
                  onChange={(value) => update("seasons", guide.seasons.map((s, i) => (i === index ? { ...s, weather: value } : s)))}
                />
                <LabeledInput
                  label="Highlights"
                  value={season.highlights}
                  onChange={(value) => update("seasons", guide.seasons.map((s, i) => (i === index ? { ...s, highlights: value } : s)))}
                />
                <LabeledInput
                  label="Travel tip"
                  value={season.tip}
                  onChange={(value) => update("seasons", guide.seasons.map((s, i) => (i === index ? { ...s, tip: value } : s)))}
                />
              </div>
              <div className="w-full sm:w-48">
                <LabeledSelect
                  label="Recommended season?"
                  value={season.best ? "yes" : "no"}
                  onChange={(value) => update("seasons", guide.seasons.map((s, i) => (i === index ? { ...s, best: value === "yes" } : s)))}
                  options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes — best" }]}
                />
              </div>
            </RowCard>
          ))}
        </div>
        <AddRowButton
          onClick={() =>
            update("seasons", [
              ...guide.seasons,
              { name: "", months: "", icon: "ri-sun-line", weather: "", highlights: "", tip: "", best: false } as Season,
            ])
          }
          label="Add season"
        />
      </SectionCard>

      {/* Activities */}
      <SectionCard icon="ri-run-line" title="Things to Do" subtitle="The activity cards on the guide.">
        <div className="space-y-3">
          {guide.activities.map((activity, index) => (
            <RowCard key={index} onRemove={() => update("activities", guide.activities.filter((_, i) => i !== index))}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <LabeledInput
                  label="Icon"
                  value={activity.icon}
                  onChange={(value) => update("activities", guide.activities.map((a, i) => (i === index ? { ...a, icon: value } : a)))}
                  placeholder="ri-ship-2-line"
                />
                <LabeledInput
                  label="Title"
                  value={activity.title}
                  onChange={(value) => update("activities", guide.activities.map((a, i) => (i === index ? { ...a, title: value } : a)))}
                />
                <LabeledInput
                  label="Description"
                  value={activity.text}
                  onChange={(value) => update("activities", guide.activities.map((a, i) => (i === index ? { ...a, text: value } : a)))}
                />
              </div>
            </RowCard>
          ))}
        </div>
        <AddRowButton
          onClick={() => update("activities", [...guide.activities, { icon: "ri-ship-2-line", title: "", text: "" } as Activity])}
          label="Add activity"
        />
      </SectionCard>

      {/* How to reach */}
      <SectionCard icon="ri-road-map-line" title="How to Reach" subtitle="Travel guidance shown on the guide.">
        <div className="space-y-3">
          {guide.reach.map((item, index) => (
            <RowCard key={index} onRemove={() => update("reach", guide.reach.filter((_, i) => i !== index))}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <LabeledInput
                  label="Icon"
                  value={item.icon}
                  onChange={(value) => update("reach", guide.reach.map((r, i) => (i === index ? { ...r, icon: value } : r)))}
                  placeholder="ri-plane-line"
                />
                <LabeledInput
                  label="Mode"
                  value={item.mode}
                  onChange={(value) => update("reach", guide.reach.map((r, i) => (i === index ? { ...r, mode: value } : r)))}
                />
                <LabeledInput
                  label="Detail"
                  value={item.detail}
                  onChange={(value) => update("reach", guide.reach.map((r, i) => (i === index ? { ...r, detail: value } : r)))}
                />
              </div>
            </RowCard>
          ))}
        </div>
        <AddRowButton
          onClick={() => update("reach", [...guide.reach, { icon: "ri-roadster-line", mode: "", detail: "" } as Reach])}
          label="Add travel option"
        />
      </SectionCard>

      {/* Photos */}
      <SectionCard icon="ri-image-2-line" title="Photos" subtitle="The guide gallery and the full-width photo band.">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">Gallery</p>
        <div className="space-y-3">
          {guide.gallery.map((image, index) => (
            <RowCard key={index} onRemove={() => update("gallery", guide.gallery.filter((_, i) => i !== index))}>
              <LabeledInput
                label="Image URL"
                value={image.src}
                onChange={(value) => update("gallery", guide.gallery.map((g, i) => (i === index ? { ...g, src: value } : g)))}
              />
              <LabeledInput
                label="Alt text"
                value={image.alt}
                onChange={(value) => update("gallery", guide.gallery.map((g, i) => (i === index ? { ...g, alt: value } : g)))}
              />
            </RowCard>
          ))}
        </div>
        <AddRowButton
          onClick={() => update("gallery", [...guide.gallery, { src: "", alt: "" } as GuideImage])}
          label="Add gallery photo"
        />

        <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2 mt-6">
          Full-width photo band
        </p>
        <div className="space-y-3">
          {guide.bandImages.map((image, index) => (
            <RowCard key={index} onRemove={() => update("bandImages", guide.bandImages.filter((_, i) => i !== index))}>
              <LabeledInput
                label="Image URL"
                value={image.src}
                onChange={(value) => update("bandImages", guide.bandImages.map((g, i) => (i === index ? { ...g, src: value } : g)))}
              />
              <LabeledInput
                label="Alt text"
                value={image.alt}
                onChange={(value) => update("bandImages", guide.bandImages.map((g, i) => (i === index ? { ...g, alt: value } : g)))}
              />
            </RowCard>
          ))}
        </div>
        <AddRowButton
          onClick={() => update("bandImages", [...guide.bandImages, { src: "", alt: "" } as GuideImage])}
          label="Add band photo"
        />
      </SectionCard>

      {/* Map */}
      <SectionCard icon="ri-map-pin-line" title="Map Embed" subtitle="The Google Maps embed URL used on the guide.">
        <LabeledInput
          label="Google Maps embed URL"
          value={guide.mapEmbed}
          onChange={(value) => update("mapEmbed", value)}
          placeholder="https://www.google.com/maps?q=…&output=embed"
        />
      </SectionCard>

      <div className="flex justify-end">
        <button type="button" onClick={save} disabled={saving} className={btnPrimary}>
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          Save guide
        </button>
      </div>

      {node}
    </div>
  );
}